import { Adjustment } from '../../report/adjustments/models/adjustment.model';
import { AdjustmentUi } from '../../report/adjustments/models/adjustment-ui.model';
import { AuthService } from '../../auth/services/auth.service';
import { Injectable } from '@angular/core';
import { ReportService } from './report.service';
import { ReportSummary } from '../models/report-summary.model';
import { FutureValuesSettings } from '../models/future-values-settings.model';
import { ReportRequest } from '../models/report-request.model';
import { RoutingService } from '../../routing/services/routing.service';
import { SettingsService } from '../../settings/services/settings.service';
import { SpecAdjustment } from '../../report/adjustments/models/spec-adjustment.model';
import { SpecificationUi } from '../../report/adjustments/models/specification-ui.model';
import { EventsService } from '../../events/services/events.service';
import { NewReportEvent } from '../../events/models/new-report-event.model';
import { ReportValuesSource } from '../../report/enums/report-values-source';
import { Report } from '../models/report.model';
import * as moment from 'moment';
import { HttpClientModule, HttpResponse, HttpClient } from '@angular/common/http';

@Injectable()
export class ReportCreationService {
    appSettings = this.settingsService.Get();
    apiUrl = this.appSettings.apiUrl;

    constructor(private httpService: HttpClient, private routingService: RoutingService, private reportService: ReportService,
                    private authService: AuthService, private settingsService: SettingsService, private eventsService: EventsService) {
  }
    saveReport(report: ReportRequest) {

      if (this.authService.getAuthorisationHeader() == null) {
          return;
      }
      
      let promise = new Promise((resolve, reject) => {
          const data = new FormData();
          const newReportEvent = new NewReportEvent();
          newReportEvent.reportType = report.reportTypeName;

          data.append('project.name', report.title);
          data.append('files', report.files[0]);
          data.append('project.reportTypeId', report.reportTypeId.toString());

          if (report.compareToDatasetDates) {
            data.append('compareToDatasetDates', report.compareToDatasetDates.toString());
          }

          if(report.compareAgainstFieldId) {
            data.append('project.compareAgainstFieldId', report.compareAgainstFieldId.toString());
          }

          if (report.reportValuesSource) {
            data.append('reportValuesSource', report.reportValuesSource.toString());

            newReportEvent.hasUsedValuation = report.reportValuesSource == ReportValuesSource.UsedValues;
            newReportEvent.hasFutureValuation = report.reportValuesSource == ReportValuesSource.FutureValues;
          }

          if (report.valuationSettings.addUsedValues) {
            data.append('project.usedValuationEnabled', 'true');
            newReportEvent.hasUsedValuation = true;
          }

          if (report.valuationSettings.addFutureValues) {
            data.append('project.futureValuationEnabled', 'true');
            newReportEvent.hasFutureValuation = true;
          }

          data.append('project.datasetDate', report.datasetDate);
          data.append('project.reportDate', report.todaysDate);
          if (report.alwaysUseCurrentDataset) {
            data.append('project.alwaysUseLatestDataset', 'true');
          }

          newReportEvent.datasetUsed = this.getDatasetUsed(report);

          if (report.valuationSettings.addFutureValues && report.valuationSettings.futureValuationSettings !== undefined) {
            data.append('project.futureValuesOutputEveryMonth',
              report.valuationSettings.futureValuationSettings.outputAllMonths ? 'true' : 'false');
            data.append('project.futureValuesOutputSpecificMonth',
              report.valuationSettings.futureValuationSettings.outputForSpecificMonth ? 'true' : 'false');
            data.append('project.FutureValueMonthToOutput',
              report.valuationSettings.futureValuationSettings.specificMonthToOutput === undefined
                ? '0'
                : report.valuationSettings.futureValuationSettings.specificMonthToOutput.toString());

            newReportEvent.futureValuationType = this.getFutureValuationTypeName(report.valuationSettings.futureValuationSettings);
          }

          if (report.valuationSettings.addTermSettings) {
            data.append('project.modifications.termModification',
              report.valuationSettings.futureValuationSettings.agreementTermModification.adjustByMonths.toString());
            data.append('project.modifications.termModificationType',
              report.valuationSettings.futureValuationSettings.agreementTermModification.termModificationType.toString());

            newReportEvent.hasContractTermAdjustments = true;
          }

          if (report.valuationSettings.addMileageSettings) {
            data.append('project.modifications.mileageModificationType',
              report.valuationSettings.futureValuationSettings.agreementEndMileageModification.mileageModificationType.toString());
            data.append('project.modifications.mileageModification',
              report.valuationSettings.futureValuationSettings.agreementEndMileageModification.adjustByValue.toString());

            newReportEvent.hasMileageAdjustments = true;
          }

          if (report.conditionType != null) {
            data.append('project.conditionTypeId', report.conditionType.toString());
            data.append('project.pricePointId', report.conditionType.toString());
          }

          newReportEvent.vehicleCondition = report.vehicleConditionName;

          const manufacturerAdjustments = report.valuationSettings.manufacturerAdjustments;
          const sectorAdjustments = report.valuationSettings.sectorAdjustments;
          const specAdjustments = report.valuationSettings.specificationAdjustments;
          let adjustCount = 0;
          if (manufacturerAdjustments != null && manufacturerAdjustments.length > 0) {
            const allAdjustmentsUi = manufacturerAdjustments.filter(a => this.hasAdjustments(a));
            const allManufacturerAdjustments = this.convertAdjustmentsUiToAdjustments(allAdjustmentsUi);
            adjustCount = this.appendAdjustmentsToFormData(data, allManufacturerAdjustments, '5', adjustCount);
          }

          if (sectorAdjustments != null && sectorAdjustments.length > 0) {
            const allAdjustmentsUi = sectorAdjustments.filter(a => this.hasAdjustments(a));
            const allSectorAdjustments = this.convertAdjustmentsUiToAdjustments(allAdjustmentsUi);
            adjustCount = this.appendAdjustmentsToFormData(data, allSectorAdjustments, '6', adjustCount);
          }

          if (specAdjustments != null && specAdjustments.length > 0) {
            const allSpecAdjustmentsUi = specAdjustments.filter(s => this.hasSpecAdjustments(s));
            const allSpecAdjustments = this.convertSpecAdjustmentsUiToSpecAdjustments(allSpecAdjustmentsUi);
            adjustCount = this.appendSpecificationAdjustmentsToFormData(data, allSpecAdjustments, adjustCount);
          }
          
          newReportEvent.hasVehicleLevelAdjustments = adjustCount>0;

          this.eventsService.recordReportCreatedEvent(newReportEvent);
          
          this.postReportDataFromService(report, data).then((reportSummary) => {
            resolve(reportSummary);
          });

      });
      return promise;
    }

    postReportDataFromService(report: ReportRequest, data: FormData) {
      const reportRoute = this.getReportRoute(report.reportTypeId);

      let promise = new Promise((resolve, reject) => {
        this.postReport(report, data)
        .toPromise()
        .then(
            (response: ReportSummary) => {
                const summarySub = this.reportService.onProjectListUpdate.subscribe(() => {
                summarySub.unsubscribe();
              });
              this.reportService.updateReportProgressFromService();
              resolve(response);
            }
        ).catch(() => {
          this.reportService.updateReportProgressFromService();
          this.eventsService.recordReportErrorScreenShownEvent();
          this.routingService.routeToReportSummaryError();
        });
      });
      return promise;
    }

  private postReport(report: ReportRequest, data: FormData) {
    const reportRoute = this.getReportRoute(report.reportTypeId);

    let headers = this.authService.getAuthorisationHeader();
    headers = headers.set('timeout', this.appSettings.reportCreationRequestTimeout.toString());

    return this.httpService.post(this.apiUrl + '/' + reportRoute, data, { headers: headers });
  }

  private hasAdjustments(adjustmentUi: AdjustmentUi): boolean {
    return (adjustmentUi.futureValueAdjustment !== 0 && adjustmentUi.futureValueAdjustment !== undefined) ||
      (adjustmentUi.usedValueAdjustment !== 0 && adjustmentUi.usedValueAdjustment !== undefined);
  }

  private hasSpecAdjustments(specificationUi: SpecificationUi): boolean {
    return (specificationUi.futureValueAdjustment !== 0 && specificationUi.futureValueAdjustment !== undefined) ||
      (specificationUi.usedValueAdjustment !== 0 && specificationUi.usedValueAdjustment !== undefined);
  }

  private convertAdjustmentsUiToAdjustments(adjustmentsUi: Array<AdjustmentUi>): Array<Adjustment> {
    const outputAdjustments = new Array<Adjustment>();

    adjustmentsUi.forEach(adjustmentUi => {
      if (adjustmentUi.futureValueAdjustment !== 0 && adjustmentUi.futureValueAdjustment !== undefined) {
        outputAdjustments.push(this.convertAdjustmentUiToAdjustment(adjustmentUi, 1));
      };
      if (adjustmentUi.usedValueAdjustment !== 0 && adjustmentUi.usedValueAdjustment !== undefined) {
        outputAdjustments.push(this.convertAdjustmentUiToAdjustment(adjustmentUi, 0));
      };
    });

    return outputAdjustments;
  }

  private convertSpecAdjustmentsUiToSpecAdjustments(specAdjustmentsUi: Array<SpecificationUi>): Array<SpecAdjustment> {
    const outputAdjustments = new Array<SpecAdjustment>();

    specAdjustmentsUi.forEach(adjustmentUi => {
      if (adjustmentUi.futureValueAdjustment !== 0 && adjustmentUi.futureValueAdjustment !== undefined) {
        outputAdjustments.push(this.convertSpecificationUiToSpecAdjustment(adjustmentUi, 1));
      };
      if (adjustmentUi.usedValueAdjustment !== 0 && adjustmentUi.usedValueAdjustment !== undefined) {
        outputAdjustments.push(this.convertSpecificationUiToSpecAdjustment(adjustmentUi, 0));
      };
    });

    return outputAdjustments;
  }

  private convertAdjustmentUiToAdjustment(adjustmentUI: AdjustmentUi, valuationType: number): Adjustment {
    const adjustment = new Adjustment();

    adjustment.valuationType = valuationType;
    adjustment.uniqueId = adjustmentUI.uniqueId;
    adjustment.value = valuationType === 0 ?
      adjustmentUI.usedValueAdjustment : adjustmentUI.futureValueAdjustment;
    adjustment.valueType = valuationType === 0 ?
      adjustmentUI.usedValueAdjustmentType : adjustmentUI.futureValueAdjustmentType;
    adjustment.vehicleType = adjustmentUI.vehicleType;

    return adjustment;
  }

  private convertSpecificationUiToSpecAdjustment(specAdjustmentUI: SpecificationUi, valuationType: number): SpecAdjustment {
    const specAdjustment = new SpecAdjustment();

    specAdjustment.valuationType = valuationType;
    specAdjustment.uniqueId = specAdjustmentUI.uniqueId;
    specAdjustment.value = valuationType === 0 ?
      specAdjustmentUI.usedValueAdjustment : specAdjustmentUI.futureValueAdjustment;
    specAdjustment.valueType = valuationType === 0 ?
      specAdjustmentUI.usedValueAdjustmentType : specAdjustmentUI.futureValueAdjustmentType;
    specAdjustment.vehicleType = specAdjustmentUI.vehicleType;
    specAdjustment.category = specAdjustmentUI.category;

    return specAdjustment;
  }

  appendAdjustmentsToFormData(formData: FormData, adjustments: Array<Adjustment>, key: string, adjustCount: number) {
    let i = adjustCount;
    adjustments.forEach(adjustment => {
      formData.append('project.adjustments[' + i + '].uniqueId', adjustment.uniqueId);
      formData.append('project.adjustments[' + i + '].valuationType', adjustment.valuationType.toString());
      formData.append('project.adjustments[' + i + '].categoryType', key);
      formData.append('project.adjustments[' + i + '].value', adjustment.value.toString());
      formData.append('project.adjustments[' + i + '].valueType', adjustment.valueType.toString());
      formData.append('project.adjustments[' + i + '].vehicleType', adjustment.vehicleType.toString());
      i++;
    });
    return i;
  }

  appendSpecificationAdjustmentsToFormData(formData: FormData, adjustments: Array<SpecAdjustment>, adjustCount: number) {
    let i = adjustCount;
    adjustments.forEach(adjustment => {
      formData.append('project.adjustments[' + i + '].uniqueId',
        adjustment.uniqueId);
      formData.append('project.adjustments[' + i + '].categoryType',
        adjustment.category.toString());
      formData.append('project.adjustments[' + i + '].valuationType',
        adjustment.valuationType.toString());
      formData.append('project.adjustments[' + i + '].value',
        adjustment.value.toString());
      formData.append('project.adjustments[' + i + '].valueType',
        adjustment.valueType.toString());
      formData.append('project.adjustments[' + i + '].vehicleType',
        adjustment.vehicleType.toString());
      i++;
    });
    return i;
  }

  getFutureValuationTypeName(futureValuesSettings: FutureValuesSettings) : string
  {
    if(futureValuesSettings.outputAllMonths)
      return 'All Months';

    if(futureValuesSettings.outputForSpecificMonth)
      return 'Specific Month'

    return 'Future Residual';

  }

  getDatasetUsed(reportRequest: ReportRequest) : string
  {
    if(reportRequest.isDefaultDataset)
      return 'default';

    return moment(reportRequest.datasetDate).format('MMMM YYYY');
  }

  getReportRoute(reportTypeId: number) : string
  {
    let route = "project";

    const reportTypeNames = Object.keys(Report.ReportTypes);
    reportTypeNames.forEach(propertyName => {
      if(Report.ReportTypes[propertyName].id==reportTypeId)
        {
          route =  this.appSettings.reportRouting
          .find(route => route.reportTypeName==Report.ReportTypes[propertyName].name).route;
        }
    });

    return route;
  }
}



// WEBPACK FOOTER //
// ./src/app/report/services/report-creation.service.ts